import { useState, useEffect, useCallback, useRef } from 'react';
import {
  adminGetInquiries,
  adminMarkFollowUpDone,
  adminSnoozeInquiryFollowUp,
} from '../../api/admin';

const POLL_INTERVAL_MS = 60 * 1000; // check every 60s
const UPCOMING_WINDOW_MS = 24 * 60 * 60 * 1000; // "upcoming" = due within next 24h

// Single source of truth for follow-up reminders across the admin panel.
// Used by both the topbar bell (badge + dropdown) and the popup toast notifications.
export function useReminders() {
  const [inquiries, setInquiries] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const shownToastIds = useRef(new Set()); // reminders already toasted this session
  const [toasts, setToasts] = useState([]);

  const refresh = useCallback(() => {
    adminGetInquiries().then((res) => {
      if (res.success) setInquiries(res.data || []);
      setLoaded(true);
    });
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  // Whenever fresh data comes in, figure out which reminders just became due
  // and haven't been toasted yet this session.
  useEffect(() => {
    if (!loaded) return;
    const now = Date.now();
    const dueNow = inquiries.filter((inq) => {
      if (!inq.follow_up_at || Number(inq.follow_up_done) === 1) return false;
      const t = new Date(inq.follow_up_at.replace(' ', 'T')).getTime();
      return t <= now;
    });

    const newlyDue = dueNow.filter((inq) => !shownToastIds.current.has(inq.id));
    if (newlyDue.length > 0) {
      newlyDue.forEach((inq) => shownToastIds.current.add(inq.id));
      setToasts((prev) => [...prev, ...newlyDue]);

      // Native browser notification too, if permitted
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        newlyDue.forEach((inq) => {
          try {
            new Notification('Follow-up reminder', {
              body: `${inq.name} — ${inq.follow_up_note || 'Time to follow up'}`,
              tag: `inquiry-${inq.id}`,
            });
          } catch (e) { /* ignore */ }
        });
      }
    }
  }, [inquiries, loaded]);

  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  const dismissToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const markDone = async (id) => {
    await adminMarkFollowUpDone(id);
    dismissToast(id);
    refresh();
  };

  const snooze = async (id, minutes) => {
    await adminSnoozeInquiryFollowUp(id, minutes);
    shownToastIds.current.delete(id); // allow it to toast again when it's due
    dismissToast(id);
    refresh();
  };

  const now = Date.now();
  const overdue = inquiries.filter((inq) => {
    if (!inq.follow_up_at || Number(inq.follow_up_done) === 1) return false;
    return new Date(inq.follow_up_at.replace(' ', 'T')).getTime() <= now;
  });
  const upcoming = inquiries.filter((inq) => {
    if (!inq.follow_up_at || Number(inq.follow_up_done) === 1) return false;
    const t = new Date(inq.follow_up_at.replace(' ', 'T')).getTime();
    return t > now && t <= now + UPCOMING_WINDOW_MS;
  });

  return {
    overdue,
    upcoming,
    toasts,
    dismissToast,
    markDone,
    snooze,
    refresh,
  };
}